import { useEffect, useRef, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { athleteTarget, removeLessonVideo, useLessonVideos, type LessonVideo } from '../api/lesson-videos.js';
import { VideoNote } from './video-note.js';
import { localThumbnail, onSendFinished, sendVideo, settle, useVideoSends } from '../uploads/video-sends.js';
import { SendingRow } from './sending-row.js';
import { day, dayAndTime, length } from './video-format.js';
import { VideoPlayer } from './video-player.js';
import { BusyLabel } from './busy-label.js';

/**
 * "My videos" on an athlete's lesson (Athlete Portal canvas row 3, approved 16
 * October). The behaviour is the explanation:
 *
 *   - each video: its thumbnail (tap to play), length, when it was sent, and
 *     when the coach opened it; Remove only while the coach has not opened it.
 *     No Replace (ruled 16 October): remove, then send another;
 *   - a video on its way: its thumbnail, a bar, and Stop;
 *   - a video that failed says so, with Remove;
 *   - a video over 30 seconds is never a row: no thumbnail, no button, only the
 *     sentence Matthew gave (16 October), until another video is chosen;
 *   - under each video, the athlete's note and the coach's reply (video-note.tsx);
 *   - "Send your coach a video", drawn on the lesson that takes videos, and
 *     greyed while no place is free, counting sends still on their way.
 */
export function LessonVideos({ lessonId, coachName }: { lessonId: string; coachName: string }) {
  const getToken = useAccessToken();
  const [nonce, setNonce] = useState(0);
  const state = useLessonVideos(lessonId, nonce);
  const all = useVideoSends().filter((send) => send.lessonId === lessonId);
  // A row only once the length is known and the video is going: drawing one
  // while it is read flashed an empty box before a too-long message (16 October).
  const sends = all.filter((send) => send.state === 'sending' || send.state === 'failed' || send.state === 'done');
  const tooLong = all.some((send) => send.state === 'too-long');
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ url: string; label: string } | null>(null);

  useEffect(() => onSendFinished(() => setNonce((n) => n + 1)), []);

  // A finished send's row gives way only once its video is in the list.
  const ready = state.status === 'ready';
  const listedKey = ready ? state.data.videos.map((video) => video.id).join(',') : '';
  useEffect(() => {
    if (ready) settle(listedKey ? listedKey.split(',') : []);
  }, [ready, listedKey]);

  // The links in the list change on every read; the pictures do not. Keeping
  // the first link for each video (they work for an hour) stops every
  // thumbnail repainting when the list is read again (16 October).
  const links = useRef(new Map<string, { thumbnail: string | null; video: string; at: number }>());

  if (state.status !== 'ready') return null;
  const { open, places_left: placesLeft } = state.data;
  const now = Date.now();
  const videos = state.data.videos.map((video) => {
    const kept = links.current.get(video.id);
    if (kept && now - kept.at < 45 * 60 * 1000) {
      return { ...video, thumbnail_url: localThumbnail(video.id) ?? kept.thumbnail, video_url: kept.video };
    }
    links.current.set(video.id, { thumbnail: video.thumbnail_url, video: video.video_url, at: now });
    return { ...video, thumbnail_url: localThumbnail(video.id) ?? video.thumbnail_url };
  });
  const fromCoach = state.data.coach_videos;
  const mine = open || videos.length > 0 || sends.length > 0;
  if (!mine && fromCoach.length === 0) return null;

  const onTheirWay = all.filter((send) => send.state === 'reading' || send.state === 'sending').length;
  const canSend = open && placesLeft - onTheirWay > 0;

  function picked(file: File | undefined) {
    if (file) sendVideo(getToken, lessonId, file, athleteTarget(lessonId));
    if (picker.current) picker.current.value = '';
  }

  async function remove(video: LessonVideo) {
    setBusy(video.id);
    try {
      await removeLessonVideo(getToken, lessonId, video.id);
    } finally {
      setBusy(null);
      setNonce((n) => n + 1);
    }
  }

  const coach = coachName ? `Coach ${coachName.split(' ')[0]}` : 'Coach';

  return (
    <>
      {fromCoach.length > 0 ? (
        <>
          <span className="section-label">From {coach}</span>
          {fromCoach.map((video) => (
            <div className="video-row" key={video.id}>
              <button
                type="button"
                className="thumb"
                aria-label={`Play ${coach}'s video from ${day(video.sent_at)}`}
                onClick={() => setPlaying({ url: video.video_url, label: `${coach}'s video, ${day(video.sent_at)}` })}
              >
                {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : null}
                <span className="dur">{length(video.duration_seconds)}</span>
              </button>
              <div className="hour-row-text">
                <span className="name">{dayAndTime(video.sent_at)}</span>
              </div>
            </div>
          ))}
        </>
      ) : null}

      {mine ? <span className="section-label">My videos</span> : null}

      {tooLong ? (
        <p className="form-note" role="alert">
          You must choose a video less than 30 secs in length.
        </p>
      ) : null}

      {sends.map((send) => (
        <SendingRow key={send.key} send={send} />
      ))}

      {videos.map((video) => (
        <div className="video-row" key={video.id}>
          <button
            type="button"
            className="thumb"
            aria-label={`Play the video sent ${day(video.sent_at)}`}
            onClick={() => setPlaying({ url: video.video_url, label: `Your video, ${day(video.sent_at)}` })}
          >
            {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : null}
            <span className="dur">{length(video.duration_seconds)}</span>
          </button>
          <div className="hour-row-text">
            <span className="name">{dayAndTime(video.sent_at)}</span>
            {video.opened_at ? <span className="detail">Opened {day(video.opened_at)}</span> : null}
          </div>
          {video.can_change ? (
            <div className="actions">
              <button type="button" onClick={() => void remove(video)} disabled={busy !== null} aria-busy={busy === video.id}>
                <BusyLabel busy={busy === video.id} working="Removing…">
                  Remove
                </BusyLabel>
              </button>
            </div>
          ) : null}
          <VideoNote lessonId={lessonId} video={video} coachName={coachName} />
        </div>
      ))}

      {open ? (
        <button type="button" className="primary" onClick={() => picker.current?.click()} disabled={!canSend}>
          Send your coach a video
        </button>
      ) : null}

      <input ref={picker} type="file" accept="video/*" hidden onChange={(event) => picked(event.target.files?.[0])} />

      {playing ? <VideoPlayer url={playing.url} label={playing.label} onClose={() => setPlaying(null)} /> : null}
    </>
  );
}
